import { useQuery } from 'react-query';
import { getSubscriptionEventsUseCases } from '@subscriptions/composition';
import type { SubscriptionEventViewDto } from '@subscriptions/application/dtos';

interface UseSubscriptionEventsReturn {
  events: SubscriptionEventViewDto[];
  isFetching: boolean;
  isError: boolean;
}

/**
 * Hook for the collection-status events (`GET /v1/events`).
 *
 * Powers the fixed error banners stacked at the top of the subscriptions domain.
 * Consumes the use case through the composition root (never imports infrastructure).
 * On error it returns an empty list so the banners simply do not render.
 *
 * @returns The list of events plus query state flags
 */
export const useSubscriptionEvents = (): UseSubscriptionEventsReturn => {
  const { listSubscriptionEvents } = getSubscriptionEventsUseCases();

  const { data, isFetching, isError } = useQuery<SubscriptionEventViewDto[], Error>(
    ['subscriptions', 'events'],
    () => listSubscriptionEvents.execute(),
    {
      refetchOnWindowFocus: false,
    },
  );

  return {
    events: data ?? [],
    isFetching,
    isError,
  };
};
